/**
 * Category Mapping
 * Maps detailed subcategories to database vendor_category enum values
 * Database enum: 'photography', 'videography', 'catering', 'decoration', 'music', 'makeup', 'mehndi', 'venue', 'planning', 'other'
 */

export const VENDOR_CATEGORIES = {
    PHOTOGRAPHY: 'photography',
    VIDEOGRAPHY: 'videography',
    CATERING: 'catering',
    DECORATION: 'decoration',
    MUSIC: 'music',
    MAKEUP: 'makeup',
    MEHNDI: 'mehndi',
    VENUE: 'venue',
    PLANNING: 'planning',
    OTHER: 'other'
};

/**
 * Maps subcategory slugs to main database categories
 */
export const SUBCATEGORY_TO_MAIN_CATEGORY = {
    // Photography & Videography
    'wedding-photographers': VENDOR_CATEGORIES.PHOTOGRAPHY,
    'photography': VENDOR_CATEGORIES.PHOTOGRAPHY,
    'videographers': VENDOR_CATEGORIES.VIDEOGRAPHY,
    'videography': VENDOR_CATEGORIES.VIDEOGRAPHY,
    'photo-booth': VENDOR_CATEGORIES.PHOTOGRAPHY,
    'drone-coverage': VENDOR_CATEGORIES.VIDEOGRAPHY,

    // Entertainment / Music
    'dj-services': VENDOR_CATEGORIES.MUSIC,
    'music': VENDOR_CATEGORIES.MUSIC,
    'dhol-bhangra': VENDOR_CATEGORIES.MUSIC,
    'mc-hosts': VENDOR_CATEGORIES.OTHER,
    'live-bands': VENDOR_CATEGORIES.MUSIC,
    'folk-dancers': VENDOR_CATEGORIES.OTHER,
    'djs': VENDOR_CATEGORIES.MUSIC,

    // Beauty & Grooming
    'bridal-makeup': VENDOR_CATEGORIES.MAKEUP,
    'makeup': VENDOR_CATEGORIES.MAKEUP,
    'hair-stylists': VENDOR_CATEGORIES.MAKEUP,
    'mehndi-artists': VENDOR_CATEGORIES.MEHNDI,
    'mehndi': VENDOR_CATEGORIES.MEHNDI,
    'groom-services': VENDOR_CATEGORIES.OTHER,

    // Cultural & Ritual Services
    'turban-tying': VENDOR_CATEGORIES.OTHER,
    'ritual-helpers': VENDOR_CATEGORIES.OTHER,
    'pooja-services': VENDOR_CATEGORIES.OTHER,
    'attire-dressing': VENDOR_CATEGORIES.OTHER,

    // Venues & Catering
    'banquet-halls': VENDOR_CATEGORIES.VENUE,
    'venue': VENDOR_CATEGORIES.VENUE,
    'venues': VENDOR_CATEGORIES.VENUE,
    'destination-venues': VENDOR_CATEGORIES.VENUE,
    'caterers': VENDOR_CATEGORIES.CATERING,
    'catering': VENDOR_CATEGORIES.CATERING,
    'sweet-shops': VENDOR_CATEGORIES.CATERING,

    // Decor & Production
    'event-decor': VENDOR_CATEGORIES.DECORATION,
    'decoration': VENDOR_CATEGORIES.DECORATION,
    'mandap-design': VENDOR_CATEGORIES.DECORATION,
    'led-screens': VENDOR_CATEGORIES.OTHER,
    'furniture-rentals': VENDOR_CATEGORIES.OTHER,

    // Attire & Accessories
    'bridal-outfits': VENDOR_CATEGORIES.OTHER,
    'jewelry-rental': VENDOR_CATEGORIES.OTHER,
    'designers-boutiques': VENDOR_CATEGORIES.OTHER,

    // Planning & Logistics
    'wedding-planners': VENDOR_CATEGORIES.PLANNING,
    'planning': VENDOR_CATEGORIES.PLANNING,
    'day-coordinators': VENDOR_CATEGORIES.PLANNING,
    'budget-planning': VENDOR_CATEGORIES.PLANNING,
    'baraat-rentals': VENDOR_CATEGORIES.OTHER,

    // Stationery & Favors
    'invitations': VENDOR_CATEGORIES.OTHER,
    'digital-invites': VENDOR_CATEGORIES.OTHER,
    'return-gifts': VENDOR_CATEGORIES.OTHER,

    // Accommodations
    'hotel-blocks': VENDOR_CATEGORIES.OTHER,
    'guest-management': VENDOR_CATEGORIES.PLANNING,
    'hospitality-kits': VENDOR_CATEGORIES.OTHER,

    // Legacy/Other
    'other': VENDOR_CATEGORIES.OTHER
};

/**
 * Get main category from subcategory slug
 */
export const getMainCategory = (subcategorySlug) => {
    return SUBCATEGORY_TO_MAIN_CATEGORY[subcategorySlug] || VENDOR_CATEGORIES.OTHER;
};

/**
 * Category display information with proper mapping
 */
export const CATEGORY_INFO = {
    [VENDOR_CATEGORIES.PHOTOGRAPHY]: {
        title: 'Photography',
        description: 'Professional wedding photographers',
        icon: 'Camera',
        subcategories: ['wedding-photographers', 'photo-booth', 'drone-coverage']
    },
    [VENDOR_CATEGORIES.VIDEOGRAPHY]: {
        title: 'Videography',
        description: 'Cinematic wedding videography',
        icon: 'Video',
        subcategories: ['videographers', 'drone-coverage']
    },
    [VENDOR_CATEGORIES.CATERING]: {
        title: 'Catering',
        description: 'Authentic cuisine for your celebration',
        icon: 'ChefHat',
        subcategories: ['caterers', 'sweet-shops']
    },
    [VENDOR_CATEGORIES.DECORATION]: {
        title: 'Decoration',
        description: 'Beautiful event decor and mandap design',
        icon: 'Flower',
        subcategories: ['event-decor', 'mandap-design']
    },
    [VENDOR_CATEGORIES.MUSIC]: {
        title: 'Music & DJs',
        description: 'Entertainment for your celebration',
        icon: 'Music',
        subcategories: ['dj-services', 'dhol-bhangra', 'live-bands', 'djs']
    },
    [VENDOR_CATEGORIES.MAKEUP]: {
        title: 'Makeup & Beauty',
        description: 'Bridal beauty services',
        icon: 'Palette',
        subcategories: ['bridal-makeup', 'hair-stylists']
    },
    [VENDOR_CATEGORIES.MEHNDI]: {
        title: 'Mehndi Artists',
        description: 'Traditional henna artistry',
        icon: 'Brush',
        subcategories: ['mehndi-artists']
    },
    [VENDOR_CATEGORIES.VENUE]: {
        title: 'Venues',
        description: 'Perfect spaces for your celebration',
        icon: 'Building',
        subcategories: ['banquet-halls', 'destination-venues', 'venues']
    },
    [VENDOR_CATEGORIES.PLANNING]: {
        title: 'Planning Services',
        description: 'Wedding planning and coordination',
        icon: 'Calendar',
        subcategories: ['wedding-planners', 'day-coordinators', 'budget-planning', 'guest-management']
    },
    [VENDOR_CATEGORIES.OTHER]: {
        title: 'Other Services',
        description: 'Additional wedding services',
        icon: 'Grid3X3',
        subcategories: ['mc-hosts', 'folk-dancers', 'groom-services', 'turban-tying', 'ritual-helpers',
            'pooja-services', 'attire-dressing', 'led-screens', 'furniture-rentals',
            'bridal-outfits', 'jewelry-rental', 'designers-boutiques', 'baraat-rentals',
            'invitations', 'digital-invites', 'return-gifts', 'hotel-blocks', 'hospitality-kits']
    }
};

/**
 * Get all subcategories for a main category
 */
export const getSubcategories = (mainCategory) => {
    return CATEGORY_INFO[mainCategory]?.subcategories || [];
};

/**
 * Check if a slug is a valid subcategory
 */
export const isValidSubcategory = (slug) => {
    return slug in SUBCATEGORY_TO_MAIN_CATEGORY;
};
